'use client';
import { type ForwardedRef, forwardRef } from 'react';
import { t } from 'i18next';
import { CircleFlag } from 'react-circle-flags';

// Generic dropdown
import { SelectDropdown, type SelectOption } from './select-dropdown.tsx';

// Country interface
import type { CountryCountryReadModel } from '@r/api/generated/client.schemas.ts';
export type ApiCountry = CountryCountryReadModel;

// Dropdown props
interface CountryDropdownProps {
  options?: ApiCountry[];
  loading: boolean;
  onChange?: (country: ApiCountry) => void;
  defaultValue?: string;
  disabled?: boolean;
  placeholder?: string;
  slim?: boolean;
}

const CountryDropdownComponent = (
  {
    options = [],
    onChange,
    defaultValue,
    loading = false,
    disabled = false,
    placeholder = 'Select a country',
    slim = false,
    ...props
  }: CountryDropdownProps,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  // Convert ApiCountry to SelectOption
  const selectOptions: SelectOption<ApiCountry>[] = options.map((country) => ({
    value: country.alpha3,
    label: country.name,
    data: country
  }));

  // Custom render for country options
  const renderCountryOption = (option: SelectOption<ApiCountry>) => (
    <>
      <div className="inline-flex overflow-hidden justify-center items-center w-5 h-5 rounded-full shrink-0">
        <CircleFlag countryCode={option.data?.alpha2.toLowerCase() || ''} height={20} />
      </div>
      <span className="overflow-hidden whitespace-nowrap text-ellipsis">{option.label}</span>
    </>
  );

  // Custom render for selected country in trigger
  const renderCountryTrigger = (selectedOption: SelectOption<ApiCountry> | undefined) => (
    <>
      <div className="inline-flex overflow-hidden justify-center items-center w-5 h-5 rounded-full shrink-0">
        <CircleFlag countryCode={selectedOption?.data?.alpha2.toLowerCase() || ''} height={20} />
      </div>
      {slim === false && (
        <span className="overflow-hidden whitespace-nowrap text-ellipsis">
          {selectedOption?.label}
        </span>
      )}
    </>
  );

  return (
    <SelectDropdown<ApiCountry>
      ref={ref}
      options={selectOptions}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      slim={slim}
      searchPlaceholder={t('common.label.search.by', { field: t('label.company.country') })}
      renderOption={renderCountryOption}
      renderTrigger={renderCountryTrigger}
      onChange={(option) => {
        if (option?.data) {
          onChange?.(option.data);
        }
      }}
      defaultValue={defaultValue}
      {...props}
    />
  );
};

CountryDropdownComponent.displayName = 'CountryDropdownComponent';

export const CountryDropdown = forwardRef(CountryDropdownComponent);
